import { pickCardiopulmonaryDetrendedSignal } from "./cardiopulmonaryView";
import { pickHeartDxHrBpm } from "./heartdx";
import { pickHrSignalQuality, pickRr, pickRrSignalQuality } from "./signalQuality";
import { readFiniteNumber } from "./viewTimeSeries";

function normalizeOptionalString(value) {
  if (value === undefined || value === null) {
    return "";
  }

  const normalizedValue = String(value).trim();
  return normalizedValue.length > 0 ? normalizedValue : "";
}

function firstFiniteNumber(...values) {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === "string" && value.trim()) {
      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        return numericValue;
      }
    }
  }

  return null;
}

function pickSummaryWorkflow(docData) {
  const summary = docData?.resultSummary?.sixMinuteWalk;
  return summary && typeof summary === "object" ? summary : null;
}

export function pickSixMinuteWalkWorkflow(docData) {
  if (docData?.workflow && typeof docData.workflow === "object") {
    return docData.workflow;
  }

  return pickSummaryWorkflow(docData);
}

export function pickSixMinuteWalkAssessmentId(docData) {
  return normalizeOptionalString(pickSixMinuteWalkWorkflow(docData)?.assessmentId) || null;
}

export function pickSixMinuteWalkStage(docData) {
  const rawStage = normalizeOptionalString(pickSixMinuteWalkWorkflow(docData)?.stage);
  return rawStage ? rawStage.toLowerCase() : null;
}

export function pickSixMinuteWalkPairedMeasurementId(docData) {
  const workflow = pickSixMinuteWalkWorkflow(docData);

  return (
    normalizeOptionalString(workflow?.pairedMeasurementId) ||
    normalizeOptionalString(workflow?.preMeasurementId) ||
    normalizeOptionalString(workflow?.pairedRecordingId) ||
    null
  );
}

export function pickSixMinuteWalkWalkSummary(docData) {
  const walkSummary = pickSixMinuteWalkWorkflow(docData)?.walk;
  return walkSummary && typeof walkSummary === "object" ? walkSummary : null;
}

function normaliseRouteTracePoint(point, index = 0) {
  const xMeters = firstFiniteNumber(
    point?.xMeters,
    point?.x,
    point?.eastMeters,
  );
  const yMeters = firstFiniteNumber(
    point?.yMeters,
    point?.y,
    point?.northMeters,
  );

  if (xMeters == null || yMeters == null) {
    return null;
  }

  return {
    xMeters,
    yMeters,
    tSeconds: firstFiniteNumber(
      point?.tSeconds,
      point?.seconds,
      point?.t,
      point?.elapsedSeconds,
      index,
    ),
    totalDistanceMeters: firstFiniteNumber(
      point?.totalDistanceMeters,
      point?.distanceMeters,
      point?.distanceMetres,
    ),
  };
}

export function pickSixMinuteWalkRouteTrace(docData) {
  const walkSummary = pickSixMinuteWalkWalkSummary(docData);
  const rawTrace =
    walkSummary?.routeTrace ??
    walkSummary?.route ??
    walkSummary?.routePoints ??
    walkSummary?.path ??
    [];

  if (!Array.isArray(rawTrace)) {
    return [];
  }

  return rawTrace
    .map((point, index) => normaliseRouteTracePoint(point, index))
    .filter(Boolean);
}

export function pickSixMinuteWalkDistanceMeters(docData) {
  const walkSummary = pickSixMinuteWalkWalkSummary(docData);

  return firstFiniteNumber(
    walkSummary?.sixMinuteDistanceMeters,
    walkSummary?.sixMinuteDistanceMetres,
    walkSummary?.distanceMeters,
    walkSummary?.distanceMetres,
    walkSummary?.totalDistanceMeters,
  );
}

export function pickSixMinuteWalkTotalTrackedDistanceMeters(docData) {
  const walkSummary = pickSixMinuteWalkWalkSummary(docData);

  return firstFiniteNumber(
    walkSummary?.totalDistanceMeters,
    walkSummary?.totalDistanceMetres,
    walkSummary?.trackedDistanceMeters,
    walkSummary?.distanceMeters,
    walkSummary?.distanceMetres,
  );
}

export function pickSixMinuteWalkRecoveryDelayMs(docData) {
  const workflow = pickSixMinuteWalkWorkflow(docData);

  return firstFiniteNumber(
    workflow?.recoveryDelayMs,
    workflow?.postWalkDelayMs,
  );
}

export function pickSixMinuteWalkRespiratoryRate(docData) {
  return pickRr(docData);
}

export function pickSixMinuteWalkRespiratoryQuality(docData) {
  return pickRrSignalQuality(docData);
}

export function pickSixMinuteWalkHeartRate(docData) {
  return pickHeartDxHrBpm(docData);
}

export function pickSixMinuteWalkCardiacQuality(docData) {
  return pickHrSignalQuality(docData);
}

export function pickSixMinuteWalkDetrendedSignal(docData) {
  return pickCardiopulmonaryDetrendedSignal(docData);
}

function isSixMinuteWalkEntry(entry) {
  const variant = normalizeOptionalString(entry?.variant || entry?.collectionName);
  return variant === "RecordingSixMinuteWalk";
}

function normalizeQualityStatus(value) {
  const normalizedValue = normalizeOptionalString(value).toLowerCase();

  if (!normalizedValue) {
    return null;
  }

  if (
    normalizedValue === "pass" ||
    normalizedValue === "passed" ||
    normalizedValue === "good" ||
    normalizedValue === "very good"
  ) {
    return "pass";
  }

  return "fail";
}

function combineQualityStatuses(...values) {
  const normalizedValues = values.map(normalizeQualityStatus).filter(Boolean);

  if (!normalizedValues.length) {
    return null;
  }

  return normalizedValues.includes("fail") ? "fail" : "pass";
}

function getEntrySignalQualityStatus(entry) {
  return (
    entry?.signalQualityStatus ??
    entry?.resultSummary?.signalQualityStatus ??
    entry?.resultSummary?.respiratoryQualityStatus ??
    null
  );
}

function getEntryCardiacQualityStatus(entry) {
  return (
    entry?.cardiacQualityStatus ??
    entry?.resultSummary?.cardiacQualityStatus ??
    null
  );
}

function buildSixMinuteWalkCombinedEntry(preEntry, postEntry) {
  const postSummary =
    postEntry?.resultSummary && typeof postEntry.resultSummary === "object"
      ? postEntry.resultSummary
      : {};
  const combinedSignalQualityStatus = combineQualityStatuses(
    getEntrySignalQualityStatus(preEntry),
    getEntrySignalQualityStatus(postEntry),
  );
  const combinedCardiacQualityStatus = combineQualityStatuses(
    getEntryCardiacQualityStatus(preEntry),
    getEntryCardiacQualityStatus(postEntry),
  );

  return {
    ...postEntry,
    signalQualityStatus:
      combinedSignalQualityStatus || postEntry?.signalQualityStatus || null,
    cardiacQualityStatus:
      combinedCardiacQualityStatus || postEntry?.cardiacQualityStatus || null,
    resultSummary: {
      ...postSummary,
      signalQualityStatus:
        combinedSignalQualityStatus || postSummary?.signalQualityStatus || null,
      respiratoryQualityStatus:
        combinedSignalQualityStatus ||
        postSummary?.respiratoryQualityStatus ||
        null,
      cardiacQualityStatus:
        combinedCardiacQualityStatus ||
        postSummary?.cardiacQualityStatus ||
        null,
    },
    sixMinuteWalkAssessment: {
      assessmentId: pickSixMinuteWalkAssessmentId(postEntry),
      preEntry,
      postEntry,
    },
  };
}

export function collapseSixMinuteWalkMeasurementEntries(entries = []) {
  const groupedEntries = new Map();

  entries.forEach((entry) => {
    if (!isSixMinuteWalkEntry(entry)) {
      return;
    }

    const assessmentId = pickSixMinuteWalkAssessmentId(entry);

    if (!assessmentId) {
      return;
    }

    const key = `${entry?.uid || ""}::${assessmentId}`;
    const group = groupedEntries.get(key) || [];
    group.push(entry);
    groupedEntries.set(key, group);
  });

  const hiddenEntryKeys = new Set();
  const combinedEntriesByKey = new Map();

  groupedEntries.forEach((group) => {
    const preEntriesById = new Map();
    const preEntries = [];

    group.forEach((entry) => {
      if (pickSixMinuteWalkStage(entry) !== "pre") {
        return;
      }

      const entryId = normalizeOptionalString(entry?.docId || entry?.name);
      if (entryId) {
        preEntriesById.set(entryId, entry);
      }
      preEntries.push(entry);
    });

    group.forEach((entry) => {
      if (pickSixMinuteWalkStage(entry) !== "post") {
        return;
      }

      const pairedMeasurementId = pickSixMinuteWalkPairedMeasurementId(entry);
      const preEntry =
        (pairedMeasurementId ? preEntriesById.get(pairedMeasurementId) : null) ||
        (preEntries.length === 1 ? preEntries[0] : null);

      if (!preEntry) {
        return;
      }

      const hiddenKey = `${preEntry?.uid || ""}::${preEntry?.name || preEntry?.docId || ""}`;
      const combinedKey = `${entry?.uid || ""}::${entry?.name || entry?.docId || ""}`;

      hiddenEntryKeys.add(hiddenKey);
      combinedEntriesByKey.set(
        combinedKey,
        buildSixMinuteWalkCombinedEntry(preEntry, entry),
      );
    });
  });

  return entries.flatMap((entry) => {
    const key = `${entry?.uid || ""}::${entry?.name || entry?.docId || ""}`;

    if (hiddenEntryKeys.has(key)) {
      return [];
    }

    return [combinedEntriesByKey.get(key) || entry];
  });
}

export function hasSixMinuteWalkAssessmentPair(entry) {
  return Boolean(entry?.sixMinuteWalkAssessment?.preEntry && entry?.sixMinuteWalkAssessment?.postEntry);
}

export function readSixMinuteWalkPendingMetricValue(value) {
  return readFiniteNumber(value);
}
